import { lazy, useEffect, useState } from 'react';
import { Navigate, Route, Routes, useNavigate, useParams } from 'react-router-dom';
import { AppShell } from './app/AppShell';
import { CommandPalette } from './app/CommandPalette';
import { Celebration } from './ui/Celebration';
import { useSpotlight } from './hooks/useSpotlight';
import { useCatalog } from './stores/catalog';
import { GuestOnly, RequireAuth } from './app/RouteGuards';
import { NotFoundPage } from './app/NotFoundPage';
import { LoginPage } from './features/auth/LoginPage';
import { SignupPage } from './features/auth/SignupPage';
import { ForgotPasswordPage } from './features/auth/ForgotPasswordPage';
import { ResetPasswordPage } from './features/auth/ResetPasswordPage';
import { VerifyEmailPage } from './features/auth/VerifyEmailPage';
import { ProblemsPage } from './features/problems/ProblemsPage';
import { ProblemPage } from './features/problems/ProblemPage';
import { ContestsPage } from './features/contests/ContestsPage';
import { ContestPage } from './features/contests/ContestPage';
import { ContestArena } from './features/contests/ContestArena';
import { StandingsPage } from './features/contests/StandingsPage';
import { ProfilePage } from './features/profile/ProfilePage';
import { BlogGate } from './features/blog/BlogGate';
import { TrackerPage } from './features/tracker/TrackerPage';
import { CoursesPage } from './features/courses/CoursesPage';
import { CoursePage } from './features/courses/CoursePage';
import { useStrings } from './tenant/TenantProvider';
import { useAuthStore } from './stores/auth';
import { apiRequest } from './lib/api';
import { decodeUser } from './lib/decoders';

const BlogListPage = lazy(() => import('./features/blog/BlogListPage'));
const BlogPostPage = lazy(() => import('./features/blog/BlogPostPage'));
const BlogEditorPage = lazy(() => import('./features/blog/BlogEditorPage'));

function LegacyRedirect({ to }) {
  const params = useParams();
  return <Navigate to={to(params)} replace />;
}

function useSessionExpiry() {
  const navigate = useNavigate();
  const t = useStrings();
  useEffect(() => {
    const onExpired = () => {
      useCatalog.getState().reset();
      navigate('/login', {
        replace: true,
        state: {
          notice: { tone: 'warning', message: t('auth.sessionExpired') },
        },
      });
    };
    window.addEventListener('session-expired', onExpired);
    return () => window.removeEventListener('session-expired', onExpired);
  }, [navigate, t]);
}

export default function App() {
  const setAuth = useAuthStore((state) => state.setAuth);
  const isAuthenticated = useAuthStore((state) => state.isAuthenticated);
  const [ready, setReady] = useState(false);
  useSessionExpiry();
  useSpotlight();

  useEffect(() => {
    let cancelled = false;
    apiRequest('/auth/v2/me', {}, decodeUser).then((response) => {
      if (cancelled) return;
      if (response.success) setAuth(response.data);
      setReady(true);
    });
    return () => {
      cancelled = true;
    };
  }, [setAuth]);

  if (!ready) return <div className="app-loading" aria-busy="true" />;

  return (
    <>
      <Routes>
        <Route element={<GuestOnly />}>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/signup" element={<SignupPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
        </Route>
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        <Route path="/verify-email" element={<VerifyEmailPage />} />

        <Route element={<RequireAuth />}>
          <Route path="/problems/:problemId" element={<ProblemPage />} />
          <Route path="/courses/:courseId/problems/:problemId" element={<ProblemPage />} />
          <Route path="/contests/:contestId/arena" element={<ContestArena />} />
          <Route element={<AppShell />}>
            <Route path="/problems" element={<ProblemsPage />} />
            <Route path="/contests" element={<ContestsPage />} />
            <Route path="/contests/:contestId" element={<ContestPage />} />
            <Route path="/contests/:contestId/standings" element={<StandingsPage />} />
            <Route path="/courses" element={<CoursesPage />} />
            <Route path="/courses/:courseId" element={<CoursePage />} />
            <Route path="/tracker" element={<TrackerPage />} />
            <Route path="/profile/:userId" element={<ProfilePage />} />
            <Route path="/profile" element={<ProfilePage />} />
            <Route element={<BlogGate />}>
              <Route path="/blog" element={<BlogListPage />} />
              <Route path="/blog/new" element={<BlogEditorPage key="new" />} />
              <Route path="/blog/:postRef" element={<BlogPostPage />} />
              <Route path="/blog/:postRef/edit" element={<BlogEditorPage key="edit" />} />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Route>
        </Route>

        <Route path="/" element={<Navigate to="/problems" replace />} />
        <Route path="/explore" element={<Navigate to="/problems" replace />} />
        <Route path="/register-contest" element={<Navigate to="/contests" replace />} />
        <Route path="/problem/:id" element={<LegacyRedirect to={({ id }) => `/problems/${id}`} />} />
        <Route path="/contest/:id" element={<LegacyRedirect to={({ id }) => `/contests/${id}`} />} />
        <Route path="/contest/:id/arena" element={<LegacyRedirect to={({ id }) => `/contests/${id}/arena`} />} />
        <Route path="/contest/:id/leaderboard" element={<LegacyRedirect to={({ id }) => `/contests/${id}/standings`} />} />
      </Routes>
      {isAuthenticated && <CommandPalette />}
      <Celebration />
    </>
  );
}
